import React, { type FC } from "react";

// 8-spoke activity indicator: each spoke rotated 45deg further round the
// circle, with opacity fading in sequence to read as spinning motion.
const SPINNER_SPOKE_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

interface ReadyToGenerateProps {
  isReadyToGenerate: boolean;
  /** True while the silent container warm-up is still running after data load */
  isInitializing?: boolean;
  onReset: () => void;
  onGenerate: () => void;
}

export const ReadyToGenerate: FC<ReadyToGenerateProps> = ({
  isReadyToGenerate,
  isInitializing = false,
  onReset,
  onGenerate,
}) => {
  return (
    <div
      className="bg-[#1d1d1f] border border-white/10 rounded-lg p-6 flex items-center justify-between"
      data-testid="ready-to-generate"
    >
      <div className="flex flex-col gap-1">
        {isInitializing ? (
          <div
            className="flex items-center gap-3"
            data-testid="ready-to-generate-initializing"
          >
            <svg
              className="animate-spin h-5 w-5 text-[#fb6a07]"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              {SPINNER_SPOKE_ANGLES.map((angle, i) => (
                <rect
                  key={angle}
                  x="11"
                  y="1"
                  width="2"
                  height="6"
                  rx="1"
                  opacity={1 - i * 0.11}
                  transform={`rotate(${angle} 12 12)`}
                />
              ))}
            </svg>
            <h3
              className="text-white font-normal"
              data-testid="ready-to-generate-title"
            >
              Initializing data...
            </h3>
          </div>
        ) : (
          <>
            <h3
              className="text-white font-normal"
              data-testid="ready-to-generate-title"
            >
              Ready to Generate
            </h3>
            <p className="text-[#7e7e81] text-sm">
              {isReadyToGenerate
                ? "All required fields are complete. Click Generate Report to create your prompt."
                : "Complete the required fields above to generate your report."}
            </p>
          </>
        )}
      </div>
      <div className="flex flex-row gap-3 items-center">
        <button
          type="button"
          onClick={onReset}
          data-testid="ready-to-generate-reset-button"
          className="px-6 py-3 rounded-lg border border-[#fb6a07] bg-transparent text-white hover:bg-[#fb6a07] transition-colors font-medium"
        >
          Reset
        </button>
        <button
          type="button"
          disabled={!isReadyToGenerate}
          onClick={onGenerate}
          data-testid="ready-to-generate-generate-button"
          className={`px-6 py-3 rounded-lg transition-colors font-medium ${
            isReadyToGenerate
              ? "bg-[#fb6a07] text-white hover:bg-[#e05f06] cursor-pointer"
              : "bg-[#fb6a07] text-white opacity-50 cursor-not-allowed"
          }`}
        >
          Generate Report
        </button>
      </div>
    </div>
  );
};
